import { ApiResponse, BookingModality, Modality, ModalityIncludedExtra } from '../models/types';
import { get } from './api';
import { config } from '../config/environment';
import { formatIncludedExtraName } from '../utils/includedExtra';
import { resolveIncludeKind } from '../utils/modalityIncludes';
import { getModalityImageUrl } from '../utils/modalityImages';

const MODALITY_ORDER: BookingModality[] = ['flexible', 'asistida', 'todo_resuelto'];

const ROOM_INCLUDE = 'Sala privada totalmente equipada';

const NUMERIC_MODALITY_MAP: Record<number, BookingModality> = {
    1: 'flexible',
    2: 'asistida',
    3: 'todo_resuelto',
};

const STATIC_MODALITY_CONTENT: Record<BookingModality, Omit<Modality, 'price' | 'imageUrl' | 'modalityId' | 'includedExtras'>> = {
    flexible: {
        id: 'flexible',
        label: 'Flexible',
        subtitle: 'La libertad de celebrar a tu manera.',
        headline: 'Tú decides cómo celebrarlo',
        description:
            'Ideal para quienes quieren organizar la celebración exactamente como prefieren y añadir únicamente los servicios que necesiten.',
        includes: [ROOM_INCLUDE],
    },
    asistida: {
        id: 'asistida',
        label: 'Asistida',
        subtitle: 'Más comodidad para disfrutar.',
        headline: 'Disfruta más, preocúpate menos',
        description:
            'Incluye monitor, merienda y limpieza para que puedas dedicar más tiempo a disfrutar de tus invitados.',
        includes: [ROOM_INCLUDE, 'Merienda', 'Limpieza de Sala', 'Monitor'],
    },
    todo_resuelto: {
        id: 'todo_resuelto',
        label: 'Todo Resuelto',
        subtitle: 'La forma más cómoda de celebrar.',
        headline: 'Tú solo disfruta del momento',
        description:
            'Nos ocupamos de la decoración, la organización y los detalles para que solo tengas que disfrutar del evento.',
        includes: [
            ROOM_INCLUDE,
            'Merienda',
            'Limpieza de Sala',
            'Limpieza Terraza',
            'Decoración Básica',
            'Monitor',
        ],
    },
};

interface ModalityApiItem {
    modality_id: number;
    name: string;
    price: number | string | null;
    included_extras?: ModalityIncludedExtra[];
}

function resolveModalityId(item: ModalityApiItem): BookingModality | null {
    if (NUMERIC_MODALITY_MAP[item.modality_id]) {
        return NUMERIC_MODALITY_MAP[item.modality_id];
    }
    const normalized = item.name.toLowerCase().replace(/[\s-]+/g, '_');
    if (normalized === 'flexible') return 'flexible';
    if (normalized === 'asistida') return 'asistida';
    if (normalized === 'todo_resuelto' || normalized === 'todoresuelto') return 'todo_resuelto';
    return null;
}

function parsePrice(value: number | string | null | undefined): number | null {
    if (value === null || value === undefined || value === '') return null;
    const parsed = typeof value === 'number' ? value : parseFloat(value);
    return Number.isNaN(parsed) ? null : parsed;
}

function buildIncludes(staticIncludes: string[], includedExtras: ModalityIncludedExtra[]): string[] {
    const extras =
        includedExtras.length > 0
            ? includedExtras.map((extra) => formatIncludedExtraName(extra))
            : staticIncludes;

    // Sala privada is part of every modality; keep it first and avoid duplicates.
    const withoutRoom = extras.filter((label) => resolveIncludeKind(label) !== 'room');
    return [ROOM_INCLUDE, ...withoutRoom];
}

function mapIncludedExtra(extra: ModalityIncludedExtra): ModalityIncludedExtra {
    return {
        extra_id: extra.extra_id,
        extra_name: extra.extra_name,
        extra_abbreviation: extra.extra_abbreviation,
        included_units: extra.included_units,
    };
}

function mapApiItemToModality(
    item: ModalityApiItem,
    shopId: number,
    shopName?: string
): Modality | null {
    const id = resolveModalityId(item);
    if (!id) return null;

    const staticContent = STATIC_MODALITY_CONTENT[id];
    const includedExtras = (item.included_extras ?? []).map(mapIncludedExtra);

    return {
        id,
        modalityId: item.modality_id,
        label: staticContent.label,
        subtitle: staticContent.subtitle,
        headline: staticContent.headline,
        description: staticContent.description,
        includes: buildIncludes(staticContent.includes, includedExtras),
        includedExtras,
        price: parsePrice(item.price),
        imageUrl: getModalityImageUrl(id, shopId, shopName),
    };
}

function buildFallbackModalities(shopId: number, shopName?: string): Modality[] {
    return MODALITY_ORDER.map((id, index) => ({
        ...STATIC_MODALITY_CONTENT[id],
        modalityId: index + 1,
        includedExtras: [],
        price: id === 'flexible' ? null : null,
        imageUrl: getModalityImageUrl(id, shopId, shopName),
    }));
}

function sortModalities(modalities: Modality[]): Modality[] {
    return [...modalities].sort(
        (a, b) => MODALITY_ORDER.indexOf(a.id) - MODALITY_ORDER.indexOf(b.id)
    );
}

export interface FetchModalitiesParams {
    shopId: number;
    shopName?: string;
    turnId: number;
    year: string;
    month: string;
    day: string;
    token: string;
}

export async function fetchModalities(params: FetchModalitiesParams): Promise<Modality[]> {
    const { shopId, shopName, turnId, year, month, day, token } = params;
    const url = `${config.API_BASE_URL}/clientapi/buy/modalities/shop/${shopId}/turn/${turnId}/${year}/${month}/${day}`;

    try {
        const response: ApiResponse = await get(url, {
            headers: { Authorization: `Bearer ${token}` },
        });

        if (response.error) {
            console.warn('Modalities API returned error:', response.message);
            return buildFallbackModalities(shopId, shopName);
        }

        const data = response.message;
        if (Array.isArray(data) && data.length > 0) {
            const modalities = (data as ModalityApiItem[])
                .map((item) => mapApiItemToModality(item, shopId, shopName))
                .filter((modality): modality is Modality => modality !== null);
            if (modalities.length > 0) {
                return sortModalities(modalities);
            }
        }
    } catch (error) {
        console.warn('Modalities API not available, using fallback content:', error);
    }

    return buildFallbackModalities(shopId, shopName);
}
